/**
 * Download an email attachment through the browser's own download UI.
 *
 * The bytes are fetched here and handed to the browser as a SAME-ORIGIN blob
 * behind an `<a download>`. Two things that look like alternatives are both
 * wrong for this:
 *
 *  - **Pointing `<a download>` straight at the cross-origin presigned S3 URL.**
 *    Browsers ignore `download` on a cross-origin response and NAVIGATE the tab
 *    to the object instead — the "download opened a new tab / a PDF viewer"
 *    behaviour. Fetching the bytes ourselves guarantees a real download and an
 *    honoured filename.
 *  - **The File System Access API (`showSaveFilePicker`).** It was used here
 *    first, for a "choose where to save" dialog. That dialog is a separate OS
 *    window that steals focus and interrupts the read — and for a PDF it is the
 *    exact thing that made "download" feel like "open somewhere else". The
 *    browser's built-in download UI is the expected surface, so that is what
 *    this uses, unconditionally and on every platform.
 *
 * NOTE: this intentionally overlaps `image-editor/lib/save-blob-locally.ts`. If a
 * third caller appears, promote a single helper to `shared/storage/`.
 *
 * @param filename Suggested name including extension, e.g. "invoice.pdf".
 * @param fetchBytes Produces the file bytes.
 * @returns `'saved'` once the bytes have been handed to the browser.
 */
export async function downloadFileToChosenLocation(
  filename: string,
  fetchBytes: () => Promise<Blob>,
): Promise<'saved' | 'cancelled'> {
  const blob = await fetchBytes();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  // Kept out of the layout and out of the tab order — it exists only to be
  // clicked. Appending is required for the synthetic click in Firefox.
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revocation a tick so the browser's async download handler has
  // finished reading the blob before the URL is torn down.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  return 'saved';
}
